import { useMemo } from 'react'
import { Moment } from 'moment-timezone'
import { EventCalendar } from '@/types/Calendar'
import {
    getErrorRate,
    getKPIAbsence,
    getKPIAvailableHours,
    getKPIRegularRequest,
    getKPIRegularSlot,
    getKPIRequestedHours,
    getKPIScheduledHours,
    getKPIStaffHoursHoliday,
    getKPIStaffHoursInSection,
    getKPIStaffHoursNotInSection,
    getKPIStaffHoursTraining,
    getKPIValidatedHours,
    getSuccessRate,
    getWarningRate
} from '../KPI/getKpi.ts'

export interface PlanningKPIData {
    regularRequest: number
    regularSlot: number
    absence: number
    requestedHours: number
    scheduledHours: number
    availableHours: number
    validatedHours: number
    staffHoursInSection: number
    staffHoursNotInSection: number
    staffHoursHoliday: number
    staffHoursTraining: number
    successRate: number
    warningRate: number
    errorRate: number
}

interface UsePlanningKPIParams {
    calendarEvents: EventCalendar[]
    lastFromDate: Moment
    lastToDate: Moment
    isParent: boolean
    merger: boolean
}

export function usePlanningKPI({
    calendarEvents,
    lastFromDate,
    lastToDate,
    isParent,
    merger
}: UsePlanningKPIParams): PlanningKPIData {
    return useMemo((): PlanningKPIData => {
        if (isParent) {
            return {
                regularRequest: 0,
                regularSlot: 0,
                absence: 0,
                requestedHours: 0,
                scheduledHours: 0,
                availableHours: getKPIAvailableHours(calendarEvents, true),
                validatedHours: getKPIValidatedHours(calendarEvents, true),
                staffHoursInSection: 0,
                staffHoursNotInSection: 0,
                staffHoursHoliday: 0,
                staffHoursTraining: 0,
                successRate: 0,
                warningRate: 0,
                errorRate: 0
            }
        }

        return {
            regularRequest: getKPIRegularRequest(calendarEvents, lastFromDate, lastToDate),
            regularSlot: getKPIRegularSlot(calendarEvents, lastFromDate, lastToDate),
            absence: getKPIAbsence(calendarEvents, lastFromDate, lastToDate),
            requestedHours: getKPIRequestedHours(calendarEvents),
            scheduledHours: getKPIScheduledHours(calendarEvents, merger),
            availableHours: getKPIAvailableHours(calendarEvents, merger),
            validatedHours: getKPIValidatedHours(calendarEvents, merger),
            staffHoursInSection: getKPIStaffHoursInSection(calendarEvents),
            staffHoursNotInSection: getKPIStaffHoursNotInSection(calendarEvents),
            staffHoursHoliday: getKPIStaffHoursHoliday(calendarEvents),
            staffHoursTraining: getKPIStaffHoursTraining(calendarEvents),
            successRate: getSuccessRate(calendarEvents),
            warningRate: getWarningRate(calendarEvents),
            errorRate: getErrorRate(calendarEvents)
        }
    }, [calendarEvents, lastFromDate, lastToDate, isParent, merger])
}
